import React from 'react';
import { AppMode } from '../../enums/AppMode';
import { UserAuth } from '../../definitions/userAuth';

interface IContext {
  /**
   * Used by HeadContext to allow reseting the page title and favicon to the
   * app's default values when those are changed. For example: on the room config
   * settings page, if the user added a custom page title or favicon for their rooms.
   *
   * @see https://gitlab.com/mindtickle/dsr/dsr-client/-/blob/track/integration/src/lib/context/HeadContext.tsx
   */
  allowStandaloneCustomization: boolean;
  /**
   * Used by AppRouter to determine whether or not a <Router> from react-router-dom
   * must be used or not. The default behavior is exposing only the routes, to
   * allow the app to be used as a federated module, as in a shell/consumer app,
   * there can't be 2 instances of a Router.
   *
   * @see https://gitlab.com/mindtickle/dsr/dsr-client/-/blob/track/integration/src/components/Router/AppRouter.tsx
   */
  allowStandaloneRouting: boolean;
  /**
   * Determines a high level context for the app, whether it's running in a
   * standalone mode or embedded in a shell/consumer app. This rules upon certain
   * UI elements or behaviors.
   */
  appMode: AppMode;
  /**
   * Default path to where the root page or home page of the app must land
   * whenever the user needs to be redirected to its landing page. For example:
   * on the `/` or the `/organization/{id}` paths.
   */
  defaultPath?: string;
  /**
   * Re-exposing the userAuth from the module federation wrapper, to allow
   * any part of the app to consume it without depending on the global
   * context for it, coming from a consumer. Specially needed for the Standalone
   * version, as there's no user auth context.
   */
  userAuth?: UserAuth;
}

export const GlobalConfigurationContext = React.createContext<IContext>({
  allowStandaloneCustomization: false,
  allowStandaloneRouting: false,
  appMode: AppMode.Standalone,
  defaultPath: undefined,
  userAuth: undefined,
});

type Props = React.PropsWithChildren<Partial<IContext>>;

const GlobalConfigurationContextProvider: React.FC<Props> = (
  props: Props
): JSX.Element => {
  const {
    allowStandaloneCustomization = false,
    allowStandaloneRouting = false,
    appMode = AppMode.Standalone,
    children,
    defaultPath,
    userAuth,
  } = props;

  return (
    <GlobalConfigurationContext.Provider
      value={{
        allowStandaloneCustomization,
        allowStandaloneRouting,
        appMode,
        defaultPath,
        userAuth,
      }}
    >
      {children}
    </GlobalConfigurationContext.Provider>
  );
};

export default GlobalConfigurationContextProvider;
